import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import type { Session, User, SupabaseClient } from "@supabase/supabase-js";
import { getSupabaseClient } from "@/lib/supabase";
import { signInRedirect } from "@/hooks/useAuth";
import { Turnstile } from "@/components/ui/turnstile";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  token: string | null;
  loading: boolean;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY;

export const SupabaseAuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const sessionRef = useRef<Session | null>(null);
  sessionRef.current = session;
  const [captchaRetryKey, setCaptchaRetryKey] = useState(0);

  const [supabase] = useState<SupabaseClient | null>(() => {
    return getSupabaseClient();
  });

  // Single-use Turnstile token pump. `signInAnonymously` callers await the
  // next token via `requestCaptchaToken()`; the Turnstile callback resolves
  // one waiter at a time.
  const pendingTokenResolversRef = useRef<Array<(token: string) => void>>([]);
  const queuedTokensRef = useRef<string[]>([]);

  const handleTurnstileToken = useCallback((token: string) => {
    const resolver = pendingTokenResolversRef.current.shift();
    if (resolver) {
      resolver(token);
    } else {
      queuedTokensRef.current.push(token);
    }
  }, []);

  const requestCaptchaToken = useCallback((): Promise<string | undefined> => {
    if (!TURNSTILE_SITE_KEY) return Promise.resolve(undefined);
    const queued = queuedTokensRef.current.shift();
    if (queued) {
      return Promise.resolve(queued);
    }
    return new Promise<string | undefined>((resolve) => {
      pendingTokenResolversRef.current.push(resolve as (t: string) => void);
    });
  }, []);

  const signInAnonymouslyWithCaptcha = useCallback(
    async (client: SupabaseClient) => {
      const captchaToken = await requestCaptchaToken();
      const result = await client.auth.signInAnonymously(
        captchaToken ? { options: { captchaToken } } : undefined,
      );
      if (captchaToken && result.error) setCaptchaRetryKey((key) => key + 1);
      return result;
    },
    [requestCaptchaToken],
  );

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (cancelled) return;
      if (session) {
        setSession(session);
        setLoading(false);
      } else {
        const { error } = await signInAnonymouslyWithCaptcha(supabase);
        if (error) console.error("Anonymous sign-in failed:", error);
        if (!cancelled) setLoading(false);
      }
    });

    const { data: { subscription: sub } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_OUT') {
        setSession(null);
        // todo(yuxiang): this should probably redirect somewhere that doesn't create a whole new VM.
        const { error } = await signInAnonymouslyWithCaptcha(supabase);
        if (error) {
          console.error('Anonymous sign-in failed after logout:', error);
          setLoading(false);
        }
        return;
      }
      setSession(session);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
  }, [supabase, signInAnonymouslyWithCaptcha]);

  useEffect(() => {
    if (!supabase) return;

    const checkAndRefreshToken = async () => {
      const currentSession = sessionRef.current;
      if (!currentSession?.expires_at) return;

      const now = Math.floor(Date.now() / 1000);
      const expiresAt = currentSession.expires_at;
      const timeUntilExpiry = expiresAt - now;

      if (timeUntilExpiry < 300) {
        console.log("Token expiring soon or expired, refreshing...");
        try {
          const { error } = await supabase.auth.refreshSession();
          if (error) {
            console.error("Failed to refresh token:", error);
            await supabase.auth.signOut();
          }
        } catch (err) {
          console.error("Error refreshing token:", err);
        }
      }
    };

    checkAndRefreshToken();
    const intervalId = setInterval(checkAndRefreshToken, 60 * 1000);
    return () => clearInterval(intervalId);
  }, [supabase]);

  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has("code") || url.searchParams.has("error")) {
      url.searchParams.delete("code");
      url.searchParams.delete("error");
      url.searchParams.delete("error_description");
      window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    }
  }, [session]);

  const signOut = useCallback(async () => {
    if (!supabase) return;
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  }, [supabase]);

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        token: session?.access_token ?? null,
        loading,
        signIn: signInRedirect,
        signOut,
      }}
    >
      {TURNSTILE_SITE_KEY && !session && (
        <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center gap-6 bg-zinc-950 text-zinc-400">
          <div className="text-sm">Verifying you're human…</div>
          <Turnstile
            key={captchaRetryKey}
            siteKey={TURNSTILE_SITE_KEY}
            appearance="interaction-only"
            theme="dark"
            onToken={handleTurnstileToken}
            onError={(code) => console.error("Turnstile error:", code)}
          />
        </div>
      )}
      {children}
    </AuthContext.Provider>
  );
};

export const useSupabaseAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx)
    throw new Error(
      "useSupabaseAuth must be used within a SupabaseAuthProvider",
    );
  return ctx;
};
